import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { funnelsAPI, festAPI } from '@/lib/api';
import { Loader2, Copy, Check, QrCode, ExternalLink, Eye, MessageSquare, CalendarCheck, Handshake, Phone } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

/* Kids Launch (/10wochen) in der Funnels-Seite. Die Seite ist kein Schritt-Funnel, sondern eine Kampagnenseite mit
 * Formular — gezeigt wird deshalb der Weg dahinter: Besucher → Anfrage → Probetraining → erschienen → Vertrag. */

const quote = (v) => (v == null ? '—' : `${v.toLocaleString('de-DE')} %`);
const tag = (iso) => { try { return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }); } catch { return ''; } };

export default function KidsLaunchBlock({ tage }) {
  const [d, setD] = useState(null);
  const [laden, setLaden] = useState(true);
  const [kopiert, setKopiert] = useState(false);
  const [qrOffen, setQrOffen] = useState(false);
  const url = `${window.location.origin}/10wochen`;

  const holen = useCallback(async () => {
    setLaden(true);
    try { setD((await funnelsAPI.kidsLaunch(tage)).data); } catch { setD(null); } finally { setLaden(false); }
  }, [tage]);
  useEffect(() => { holen(); }, [holen]);

  const kopieren = async () => {
    try { await navigator.clipboard.writeText(url); setKopiert(true); setTimeout(() => setKopiert(false), 2000); } catch { /* verweigert */ }
  };

  if (laden) return <div className="flex items-center gap-2 text-muted-foreground py-10"><Loader2 className="h-4 w-4 animate-spin" /> Kids Launch wird geladen …</div>;
  if (!d) return <p className="text-sm text-muted-foreground py-10">Die Auswertung konnte nicht geladen werden.</p>;

  const stufen = [
    { label: 'Besucher der Seite', wert: d.besucher, sub: `${d.aufrufe} Aufrufe` },
    { label: 'Anfragen', wert: d.anfragen, sub: `${quote(d.quoten.besucher_anfrage)} der Besucher` },
    { label: 'Probetraining vereinbart', wert: d.probetraining, sub: `${quote(d.quoten.anfrage_probetraining)} der Anfragen` },
    { label: 'Erschienen', wert: d.erschienen, sub: `${quote(d.quoten.probetraining_erschienen)} der Termine` },
    { label: 'Vertrag', wert: d.abschluesse, sub: `${quote(d.quoten.erschienen_abschluss)} der Erschienenen` },
  ];
  const max = Math.max(...stufen.map((s) => s.wert), 1);

  return (
    <div className="space-y-5" data-testid="kids-launch-block">
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
          <div>
            <CardTitle className="text-base">Direktzugriff</CardTitle>
            <p className="text-sm text-muted-foreground mt-0.5 break-all">{url}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={kopieren}>
              {kopiert ? <Check className="h-4 w-4 mr-1.5" /> : <Copy className="h-4 w-4 mr-1.5" />}{kopiert ? 'Kopiert' : 'Link kopieren'}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setQrOffen((o) => !o)}><QrCode className="h-4 w-4 mr-1.5" /> QR-Code</Button>
            <Button size="sm" asChild><a href="/10wochen" target="_blank" rel="noreferrer"><ExternalLink className="h-4 w-4 mr-1.5" /> Seite öffnen</a></Button>
          </div>
        </CardHeader>
        {qrOffen && (
          <CardContent className="pt-0">
            <div className="flex flex-col items-center gap-2 py-2">
              <img src={festAPI.qrUrl(url)} alt="QR-Code Kids Launch" width={190} height={190} className="bg-white p-3 rounded-xl" />
              <p className="text-xs text-muted-foreground">Rechtsklick → Bild speichern, für Flyer und Aushang</p>
            </div>
          </CardContent>
        )}
      </Card>

      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        {[
          { icon: Eye, label: 'Besucher', wert: d.besucher, sub: `${d.aufrufe} Aufrufe in ${d.tage} Tagen` },
          { icon: MessageSquare, label: 'Anfragen', wert: d.anfragen, sub: `${quote(d.quoten.besucher_anfrage)} der Besucher` },
          { icon: CalendarCheck, label: 'Probetrainings', wert: d.probetraining, sub: `${d.erschienen} erschienen` },
          { icon: Handshake, label: 'Verträge', wert: d.abschluesse, sub: `${quote(d.quoten.anfrage_abschluss)} der Anfragen${d.challenge ? ` · ${d.challenge}× Challenge` : ''}` },
        ].map((k) => (
          <Card key={k.label}>
            <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5"><k.icon className="h-4 w-4" /> {k.label}</CardTitle></CardHeader>
            <CardContent><div className="text-3xl font-bold tabular-nums">{k.wert}</div><p className="text-xs text-muted-foreground mt-1">{k.sub}</p></CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Der Weg von der Seite zum Vertrag</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {stufen.map((s) => (
            <div key={s.label} className="flex items-center gap-3">
              <div className="w-44 text-xs font-medium text-right flex-shrink-0">{s.label}</div>
              <div className="flex-1 h-7 rounded-md bg-muted/50 overflow-hidden">
                <div className="h-full bg-marke rounded-md flex items-center px-2.5 transition-all" style={{ width: `${Math.max(s.wert ? 6 : 0, (s.wert / max) * 100)}%` }}>
                  <span className="text-[11px] font-semibold text-white tabular-nums">{s.wert || ''}</span>
                </div>
              </div>
              <div className="w-40 text-[11px] text-muted-foreground flex-shrink-0">{s.sub}</div>
            </div>
          ))}
          {d.verloren > 0 && <p className="text-[11px] text-muted-foreground pt-1">{d.verloren} Anfrage{d.verloren === 1 ? '' : 'n'} als verloren markiert.</p>}
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Nach Altersgruppe</CardTitle></CardHeader>
          <CardContent>
            {d.gruppen.length === 0 ? <p className="text-sm text-muted-foreground">Noch keine Anfragen im Zeitraum.</p> : (
              <table className="w-full text-sm">
                <thead><tr className="text-[11px] text-muted-foreground text-right"><th className="text-left font-medium py-1">Gruppe</th><th className="font-medium">Anfragen</th><th className="font-medium">Probetr.</th><th className="font-medium">Erschienen</th><th className="font-medium">Verträge</th></tr></thead>
                <tbody>{d.gruppen.map((g) => (
                  <tr key={g.gruppe} className="border-t border-border/50 text-right tabular-nums"><td className="text-left py-1.5 font-medium">{g.gruppe}</td><td>{g.anfragen}</td><td>{g.probetraining}</td><td>{g.erschienen}</td><td className="font-bold">{g.abschluesse}</td></tr>
                ))}</tbody>
              </table>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Woher die Anfragen kommen</CardTitle></CardHeader>
          <CardContent className="space-y-1.5">
            {d.quellen.length === 0 ? <p className="text-sm text-muted-foreground">Noch keine Anfragen im Zeitraum.</p>
              : d.quellen.map((q) => <div key={q.quelle} className="flex items-center justify-between text-sm"><span>{q.quelle}</span><span className="font-bold tabular-nums">{q.anfragen}</span></div>)}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><Phone className="h-4 w-4" /> Offene Anfragen <Badge variant="secondary">{d.offen.length}</Badge></CardTitle>
          <p className="text-xs text-muted-foreground">Angefragt, aber noch kein Probetraining vereinbart. Ein Klick öffnet den Lead.</p>
        </CardHeader>
        <CardContent>
          {d.offen.length === 0 ? <p className="text-sm text-muted-foreground">Keine offenen Anfragen.</p> : (
            <div className="divide-y divide-border/50">
              {d.offen.map((z) => (
                <Link key={z.id} to={`/leads/${z.id}`} className="flex items-center justify-between gap-3 py-2 text-sm hover:bg-muted/40 rounded px-1">
                  <span className="font-medium truncate">{z.name}</span>
                  <span className="text-xs text-muted-foreground flex-shrink-0">{z.gruppe} · {tag(z.am)}</span>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
